import { useApiQuery } from '../useApiQuery';
import { contactKeys } from '@/lib/queryKeys';
import { apiGet, operationsClient } from '@/lib/api';
import type { Contact } from '@/types/contact';
import { normalizeContact } from './useContacts';

export function useContact(id: string, options?: { enabled?: boolean; includeDeleted?: boolean }) {
  return useApiQuery<Contact>({
    queryKey: contactKeys.detail(id),
    queryFn: async () => {
      // `includeDeleted` is opt-in (the contact-detail page) so a
      // soft-deleted contact resolves instead of 404ing and can be
      // restored. Every other consumer keeps the default and still 404s on
      // deleted — they should never silently render an archived contact.
      const url = options?.includeDeleted
        ? `/contacts/${id}?includeDeleted=true`
        : `/contacts/${id}`;
      const raw = await apiGet<Record<string, unknown>>(operationsClient, url);
      return normalizeContact(raw);
    },
    enabled: Boolean(id) && (options?.enabled ?? true),
    showErrorToast: false,
    // No client-side caching — the QB-linked-note id and other QB-driven
    // fields change at webhook speed, and the operator expects live state.
    // Mirrors the notes hook so the linked-note badge stays in sync.
    staleTime: 0,
    gcTime: 0,
    refetchInterval: 5_000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
  });
}
